/** Traffic Analysis page - call volume trends, destination analysis, and traffic distribution. */
import React, { useState, useEffect } from "react";
import { useTheme, Theme } from "@mui/material/styles";
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  CircularProgress,
  Chip,
} from "@mui/material";
import {
  Refresh,
  Phone,
  Timer,
  Call,
  TrendingUp,
} from "@mui/icons-material";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { trafficApi } from "../api/client";

interface TrafficDataPoint {
  hour: string;
  calls: number;
  minutes: number;
  asr: number;
  acd: number;
}

interface DestinationTraffic {
  destination: string;
  calls: number;
  minutes: number;
  asr: number;
  acd: number;
  revenue: number;
}

interface CarrierTraffic {
  carrier: string;
  calls: number;
  minutes: number;
  asr: number;
  pdd: number | null;
}

interface HourlyPattern {
  hour: number;
  calls: number;
}

interface TrafficSummary {
  total_calls: number;
  answered_calls: number;
  total_minutes: number;
  asr_percent: number;
  acd_seconds: number;
  max_cps: number;
  peak_hour: string | null;
}

/** G1: chart colors derived from the active theme (dark-mode safe). */
function chartPalette(theme: Theme): string[] {
  return [
    theme.palette.primary.main,
    theme.palette.success.main,
    theme.palette.warning.main,
    theme.palette.error.main,
    theme.palette.secondary.main,
    theme.palette.info.main,
    theme.palette.primary.light,
  ];
}

/** Traffic Analysis page component. */
const TrafficAnalysis: React.FC = () => {
  const theme = useTheme();
  const COLORS = chartPalette(theme);
  const [period, setPeriod] = useState("last_7_days");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<TrafficSummary | null>(null);
  const [hourlyData, setHourlyData] = useState<TrafficDataPoint[]>([]);
  const [destinations, setDestinations] = useState<DestinationTraffic[]>([]);
  const [carriers, setCarriers] = useState<CarrierTraffic[]>([]);
  const [hourlyPattern, setHourlyPattern] = useState<HourlyPattern[]>([]);

  useEffect(() => {
    fetchTrafficData();
  }, [period]);

  // Live data from GET /api/v1/traffic/analysis — CDR aggregates computed
  // server-side (summary, last-24h hourly trend, typical-day pattern,
  // top destinations, carriers).
  const fetchTrafficData = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await trafficApi.getAnalysis(period);
      const data = res.data;
      setSummary(data.summary ?? null);
      setHourlyData(data.hourly ?? []);
      setDestinations(data.destinations ?? []);
      setCarriers(data.carriers ?? []);
      setHourlyPattern(data.hourly_pattern ?? []);
    } catch (err: any) {
      console.error("Error fetching traffic data:", err);
      setError(
        err?.response?.data?.detail ||
          "Failed to load live traffic data. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  const renderSummaryCards = () => {
    if (!summary) return null;

    const cards = [
      {
        title: "Total Calls",
        value: summary.total_calls.toLocaleString(),
        subtitle: `${summary.answered_calls} answered`,
        icon: <Phone />,
        color: theme.palette.primary.main,
      },
      {
        title: "Total Minutes",
        value: Math.floor(summary.total_minutes / 60).toLocaleString(),
        subtitle: `${formatDuration(summary.acd_seconds)} avg duration`,
        icon: <Timer />,
        color: theme.palette.success.main,
      },
      {
        title: "ASR",
        value: `${summary.asr_percent.toFixed(1)}%`,
        subtitle: "Answer-Seizure Ratio",
        icon: <Call />,
        color:
          summary.asr_percent >= 80
            ? theme.palette.success.main
            : theme.palette.warning.main,
      },
      {
        title: "Peak CPS",
        value: summary.max_cps.toString(),
        subtitle: `Peak hour: ${summary.peak_hour ?? "—"}`,
        icon: <TrendingUp />,
        color: theme.palette.warning.main,
      },
    ];

    return (
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {cards.map((card) => (
          <Grid item xs={12} sm={6} md={3} key={card.title}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                  <Typography variant="body2" color="text.secondary">
                    {card.title}
                  </Typography>
                  <Box sx={{ color: card.color }}>{card.icon}</Box>
                </Box>
                <Typography variant="h5" component="div" sx={{ color: card.color, fontWeight: "bold" }}>
                  {card.value}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {card.subtitle}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  };

  const renderHourlyChart = () => (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Call Volume by Hour (Last 24 Hours)
        </Typography>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={hourlyData}>
            <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
            <XAxis dataKey="hour" stroke={theme.palette.text.secondary} />
            <YAxis stroke={theme.palette.text.secondary} />
            <Tooltip />
            <Legend />
            <Line type="monotone" dataKey="calls" stroke={theme.palette.primary.main} name="Calls" strokeWidth={2} />
            <Line type="monotone" dataKey="asr" stroke={theme.palette.success.main} name="ASR %" strokeWidth={2} />
          </LineChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );

  const renderHourlyPattern = () => (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Typical Hourly Pattern (Average Day)
        </Typography>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={hourlyPattern}>
            <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
            <XAxis dataKey="hour" stroke={theme.palette.text.secondary} label={{ value: "Hour of Day", position: "insideBottom", offset: -5 }} />
            <YAxis stroke={theme.palette.text.secondary} label={{ value: "Calls", angle: -90, position: "insideLeft" }} />
            <Tooltip />
            <Bar dataKey="calls" fill={theme.palette.primary.main} name="Avg Calls" />
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );

  const renderDestinationTable = () => (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Top Destinations by Call Volume
        </Typography>
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Destination</TableCell>
                <TableCell align="right">Calls</TableCell>
                <TableCell align="right">Minutes</TableCell>
                <TableCell align="right">ASR</TableCell>
                <TableCell align="right">ACD</TableCell>
                <TableCell align="right">Revenue</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {destinations.map((dest) => (
                <TableRow key={dest.destination} hover>
                  <TableCell>{dest.destination}</TableCell>
                  <TableCell align="right">{dest.calls.toLocaleString()}</TableCell>
                  <TableCell align="right">{dest.minutes.toLocaleString()}</TableCell>
                  <TableCell align="right">
                    <Chip
                      label={`${dest.asr.toFixed(1)}%`}
                      size="small"
                      color={dest.asr >= 85 ? "success" : "warning"}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="right">{formatDuration(dest.acd)}</TableCell>
                  <TableCell align="right">${dest.revenue.toLocaleString()}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );

  const renderCarrierTable = () => (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Traffic by Carrier
        </Typography>
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Carrier</TableCell>
                <TableCell align="right">Calls</TableCell>
                <TableCell align="right">Minutes</TableCell>
                <TableCell align="right">ASR</TableCell>
                <TableCell align="right">PDD</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {carriers.map((carrier) => (
                <TableRow key={carrier.carrier} hover>
                  <TableCell>{carrier.carrier}</TableCell>
                  <TableCell align="right">{carrier.calls.toLocaleString()}</TableCell>
                  <TableCell align="right">{carrier.minutes.toLocaleString()}</TableCell>
                  <TableCell align="right">
                    <Chip
                      label={`${carrier.asr.toFixed(1)}%`}
                      size="small"
                      color={carrier.asr >= 85 ? "success" : "warning"}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="right">{carrier.pdd !== null ? `${carrier.pdd.toFixed(2)}s` : "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );

  const renderDestinationPieChart = () => {
    const chartData = destinations.map((dest) => ({
      name: dest.destination,
      value: dest.calls,
    }));

    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Call Distribution by Destination
          </Typography>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name}: ${((percent || 0) * 100).toFixed(0)}%`}
                outerRadius={100}
                fill={theme.palette.primary.main}
                dataKey="value"
              >
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    );
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Traffic Analysis
        </Typography>
        <Box display="flex" gap={2}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Period</InputLabel>
            <Select
              value={period}
              label="Period"
              onChange={(e) => setPeriod(e.target.value)}
            >
              <MenuItem value="today">Today</MenuItem>
              <MenuItem value="yesterday">Yesterday</MenuItem>
              <MenuItem value="last_7_days">Last 7 Days</MenuItem>
              <MenuItem value="last_30_days">Last 30 Days</MenuItem>
              <MenuItem value="this_month">This Month</MenuItem>
            </Select>
          </FormControl>
          <Button variant="outlined" startIcon={<Refresh />} onClick={fetchTrafficData} disabled={loading}>
            Refresh
          </Button>
        </Box>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        Traffic analysis provides insights into call patterns, peak hours, top destinations, and carrier performance.
        Use this data to optimize routing, capacity planning, and identify quality issues.
      </Alert>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" startIcon={<Refresh />} onClick={fetchTrafficData}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {!loading && !error && summary && summary.total_calls === 0 && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          No call data found for this period. Live traffic appears here once CDRs are ingested.
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {renderSummaryCards()}
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              {renderHourlyChart()}
            </Grid>
            <Grid item xs={12} md={4}>
              {renderDestinationPieChart()}
            </Grid>
          </Grid>
          {renderHourlyPattern()}
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              {renderDestinationTable()}
            </Grid>
            <Grid item xs={12} md={6}>
              {renderCarrierTable()}
            </Grid>
          </Grid>
        </>
      )}
    </Container>
  );
};

export default TrafficAnalysis;
